import analyticsService from '../services/analyticsService';

/**
 * Tracks user edits to AI-generated content
 * 
 * @param {Object} options - Tracking options
 * @param {string} options.contentType - Type of content being edited (consequence, mitigation_description, etc.)
 * @param {string} options.hazardId - ID of the hazard, or 'new' for new hazards
 * @param {string} options.oldValue - Original AI-generated content
 * @param {string} options.newValue - User-edited content
 * @param {Object} options.additionalData - Any additional contextual data for the edit
 * @returns {Promise<void>}
 */
export const trackAiContentEdit = async ({ contentType, hazardId, oldValue, newValue, additionalData = {} }) => {
  // Only track if values are different
  if (oldValue === newValue) return;
  
  await analyticsService.trackFeatureUsage('edited_ai_content', {
    content_type: contentType,
    hazard_id: hazardId || 'new',
    original_length: oldValue?.length || 0,
    new_length: newValue?.length || 0,
    edit_distance: Math.abs((newValue?.length || 0) - (oldValue?.length || 0)),
    ...additionalData
  });
};


// Tracks edits to AI-generated consequences
export const trackConsequenceEdit = async (hazard, oldValue, newValue) => {
  if (!hazard?.aiGenerated?.consequences?.includes(oldValue)) return;
  
  await trackAiContentEdit({
    contentType: 'consequence',
    hazardId: hazard.id || 'new',
    oldValue,
    newValue
  });
};


// Tracks edits to AI-generated mitigation descriptions
export const trackMitigationDescriptionEdit = async (hazard, mitigation, oldValue, newValue) => {
  if (!hazard?.aiGenerated?.mitigations || !mitigation?.aiGenerated) return;
  
  await trackAiContentEdit({
    contentType: 'mitigation_description',
    hazardId: hazard.id || 'new',
    oldValue,
    newValue,
    additionalData: {
      mitigation_id: mitigation.id,
      mitigation_type: mitigation.type
    }
  });
};


//  Tracks edits to AI-generated safety requirements
export const trackSafetyRequirementEdit = async (hazard, mitigation, oldValue, newValue) => {
  if (!hazard?.aiGenerated?.safetyRequirements || !mitigation?.aiGenerated) return;
  
  await trackAiContentEdit({
    contentType: 'safety_requirement',
    hazardId: hazard.id || 'new',
    oldValue,
    newValue,
    additionalData: {
      mitigation_id: mitigation.id
    }
  });
};


// Tracks edits to AI-generated SFAIRP statements
export const trackSfairpStatementEdit = async (hazard, oldValue, newValue) => {
  if (!hazard?.aiGenerated?.sfairpStatement) return;
  
  await trackAiContentEdit({
    contentType: 'sfairp_statement',
    hazardId: hazard.id || 'new',
    oldValue,
    newValue
  });
};


// Tracks edits to AI-generated hazard types
export const trackHazardTypeEdit = async (hazard, oldValue, newValue) => {
  if (!hazard?.aiGenerated?.type) return;
  
  await trackAiContentEdit({
    contentType: 'hazard_type',
    hazardId: hazard.id || 'new',
    oldValue,
    newValue
  });
};

// Tracks edits to AI-generated hazard sources
export const trackHazardSourceEdit = async (hazard, oldValue, newValue) => {
  if (!hazard?.aiGenerated?.hazardSource) return;
  
  await trackAiContentEdit({
    contentType: 'hazard_source',
    hazardId: hazard.id || 'new',
    oldValue,
    newValue
  });
};

// Tracks edits to AI-generated hazard descriptions
export const trackHazardDescriptionEdit = async (hazard, oldValue, newValue) => {
  if (!hazard?.aiGenerated?.description) return;
  
  await trackAiContentEdit({
    contentType: 'hazard_description',
    hazardId: hazard.id || 'new',
    oldValue,
    newValue
  });
};

// Tracks edits to AI-generated causes
export const trackCauseEdit = async (hazard, oldValue, newValue) => {
  if (!hazard?.aiGenerated?.causes?.includes(oldValue)) return;
  
  await trackAiContentEdit({
    contentType: 'cause',
    hazardId: hazard.id || 'new',
    oldValue,
    newValue
  });
};

export default {
  trackAiContentEdit,
  trackConsequenceEdit,
  trackMitigationDescriptionEdit,
  trackSafetyRequirementEdit,
  trackSfairpStatementEdit,
  trackHazardTypeEdit,
  trackHazardSourceEdit,
  trackHazardDescriptionEdit,
  trackCauseEdit
};